/* ============================================
   CART FIELDS — Gift message & dispatch date

   Gift message is per line item: every item in the cart drawer and on the cart
   page has its own checkbox and form, and the values are written straight onto
   that line as properties ("Gift To", "Gift From", "Gift Message") — which is
   what the checkout and the order show under that product.

   Writing properties re-hashes the line key, so every control still holding the
   old key (quantity input, remove link, the other rendered copy of the same
   line) is re-pointed at the new one right after the write. See rekeyLine().

   Dispatch date stays a single cart-wide field, so it lives in cart attributes
   while the customer edits and is copied onto every line item on the way to
   checkout.

   All cart writes go through one queue: Shopify rejects concurrent cart
   mutations. Listeners are delegated on `document` because the cart drawer
   replaces its markup after every cart change.
   ============================================ */
(function () {
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var DEBOUNCE = 500;

  /* ------------------------------------------------------------------
     Cart writes — one at a time
     ------------------------------------------------------------------ */

  var queue = Promise.resolve();

  function enqueue(task) {
    var result = queue.then(task, task);

    // The queue itself must never stay rejected, or every later write is skipped.
    queue = result.catch(function () {});

    return result;
  }

  function fetchCart() {
    return fetch(ROOT + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (response) {
      return response.json();
    });
  }

  /* ------------------------------------------------------------------
     Dispatch date — cart attribute
     ------------------------------------------------------------------ */

  var FIELD_ATTRIBUTES = {
    'dispatch-date': 'Dispatch Date'
  };

  /* Attributes that are also copied onto every line item at checkout. */
  var LINE_PROPERTIES = ['Dispatch Date'];

  var EMPTY = {
    'Dispatch Date': ''
  };

  var FIELD_SELECTOR = '.dispatch-date';

  var timers = new WeakMap();

  /* The drawer and the cart page each render their own copy. */
  function groupOf(element) {
    if (!element || !element.closest) {
      return null;
    }

    var field = element.closest(FIELD_SELECTOR);

    return field ? field.closest('form') || document : null;
  }

  function groups() {
    var found = [];

    document.querySelectorAll(FIELD_SELECTOR).forEach(function (field) {
      var group = field.closest('form') || document;

      if (found.indexOf(group) === -1) {
        found.push(group);
      }
    });

    return found;
  }

  function fieldOf(group, name) {
    return group.querySelector('[data-cart-field="' + name + '"]');
  }

  /* An empty value removes the attribute from the cart. */
  function collect(group) {
    var attributes = Object.assign({}, EMPTY);

    Object.keys(FIELD_ATTRIBUTES).forEach(function (name) {
      var field = fieldOf(group, name);

      if (field) {
        attributes[FIELD_ATTRIBUTES[name]] = field.value.trim();
      }
    });

    return attributes;
  }

  function applyToGroup(group, attributes) {
    Object.keys(FIELD_ATTRIBUTES).forEach(function (name) {
      var field = fieldOf(group, name);

      if (field) {
        field.value = attributes[FIELD_ATTRIBUTES[name]];
      }
    });
  }

  function mirror(source, attributes) {
    groups().forEach(function (group) {
      if (group !== source) {
        applyToGroup(group, attributes);
      }
    });
  }

  function save(group) {
    var attributes = collect(group);

    mirror(group, attributes);

    return enqueue(function () {
      return fetch(ROOT + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
        // Makes sure the request is fired even while navigating to checkout.
        keepalive: true
      }).catch(function () {});
    });
  }

  function schedule(group) {
    clearTimeout(timers.get(group));
    timers.set(
      group,
      setTimeout(function () {
        save(group);
      }, DEBOUNCE)
    );
  }

  function flush(group) {
    clearTimeout(timers.get(group));

    return save(group);
  }

  /* ------------------------------------------------------------------
     Gift message — line item properties
     ------------------------------------------------------------------ */

  var GIFT_FIELDS = {
    to: 'Gift To',
    from: 'Gift From',
    message: 'Gift Message'
  };

  var GIFT_PROPERTIES = ['Gift To', 'Gift From', 'Gift Message'];

  var giftTimers = new WeakMap();
  var statusTimers = new WeakMap();

  function giftLineOf(element) {
    return element && element.closest ? element.closest('[data-gift-line]') : null;
  }

  function giftFieldOf(container, name) {
    return container.querySelector('[data-gift-field="' + name + '"]');
  }

  function giftLinesFor(key) {
    return document.querySelectorAll('[data-gift-line][data-line-key="' + key + '"]');
  }

  /* The same line item can be on screen twice — once in the drawer, once on the
     cart page — and both copies have to show the same thing. */
  function twinsOf(container) {
    return Array.prototype.filter.call(
      giftLinesFor(container.getAttribute('data-line-key')),
      function (node) {
        return node !== container;
      }
    );
  }

  function isGiftChecked(container) {
    var toggle = container.querySelector('[data-gift-toggle]');

    return toggle ? toggle.checked : false;
  }

  /* Unticking the checkbox clears the properties, which removes them. */
  function collectGift(container) {
    var checked = isGiftChecked(container);
    var values = {};

    Object.keys(GIFT_FIELDS).forEach(function (name) {
      var field = giftFieldOf(container, name);

      values[GIFT_FIELDS[name]] = checked && field ? field.value.trim() : '';
    });

    return values;
  }

  function revealGift(container, show) {
    var fields = container.querySelector('.gift-message__fields');

    if (fields) {
      fields.toggleAttribute('hidden', !show);
    }
  }

  function updateGiftCounter(container) {
    var counter = container.querySelector('[data-gift-counter]');
    var textarea = giftFieldOf(container, 'message');

    if (!counter || !textarea) {
      return;
    }

    var max = parseInt(textarea.getAttribute('maxlength'), 10) || 200;
    var length = textarea.value.length;

    counter.textContent = length + ' / ' + max;
    counter.classList.toggle('gift-message__counter--limit', length >= max);
  }

  function applyGift(container, values, checked) {
    var toggle = container.querySelector('[data-gift-toggle]');

    if (toggle) {
      toggle.checked = checked;
    }

    Object.keys(GIFT_FIELDS).forEach(function (name) {
      var field = giftFieldOf(container, name);

      if (field) {
        field.value = values[GIFT_FIELDS[name]];
      }
    });

    revealGift(container, checked);
    updateGiftCounter(container);
  }

  function mirrorGift(container) {
    var values = collectGift(container);
    var checked = isGiftChecked(container);

    twinsOf(container).forEach(function (twin) {
      applyGift(twin, values, checked);
    });
  }

  function setGiftStatus(container, state) {
    var nodes = [container].concat(twinsOf(container));

    nodes.forEach(function (node) {
      var status = node.querySelector('[data-gift-status]');

      if (!status) {
        return;
      }

      var text =
        state === 'error'
          ? node.getAttribute('data-gift-error-text')
          : node.getAttribute('data-gift-saved-text');

      status.textContent = state ? text || '' : '';
      status.classList.toggle('gift-message__status--error', state === 'error');

      clearTimeout(statusTimers.get(status));

      if (state === 'saved') {
        statusTimers.set(
          status,
          setTimeout(function () {
            status.textContent = '';
          }, 2500)
        );
      }
    });
  }

  /* /cart/change.js replaces a line item's properties, so anything set by
     another app (or by the product page) has to be carried over. */
  function propertiesWithGift(item, values) {
    var properties = {};

    Object.keys(item.properties || {}).forEach(function (key) {
      if (GIFT_PROPERTIES.indexOf(key) === -1) {
        properties[key] = item.properties[key];
      }
    });

    GIFT_PROPERTIES.forEach(function (key) {
      properties[key] = values[key];
    });

    return properties;
  }

  function giftMatches(properties, values) {
    var current = properties || {};

    return GIFT_PROPERTIES.every(function (key) {
      return (current[key] || '') === values[key];
    });
  }

  /* The line key is a hash of the variant and its properties, so saving a gift
     message mints a new one. Everything still holding the old key — quantity
     input, plus/minus links, remove link, the twin copy of this line — is
     re-pointed, otherwise the next quantity change would fail. */
  function rekeyLine(oldKey, newKey) {
    if (!newKey || oldKey === newKey) {
      return;
    }

    document.querySelectorAll('[data-line-key="' + oldKey + '"]').forEach(function (node) {
      node.setAttribute('data-line-key', newKey);
    });

    document.querySelectorAll('a[href*="/cart/change"]').forEach(function (link) {
      var url = new URL(link.href, window.location.origin);

      if (url.searchParams.get('id') === oldKey) {
        url.searchParams.set('id', newKey);
        link.setAttribute('href', url.pathname + url.search);
      }
    });
  }

  /* The drawer re-renders itself from Liquid on `cart:refresh`. */
  function rerenderCartDrawer() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  /* The cart page has no such hook, and one holding keys it can no longer trust
     would fail on the next quantity change, so it is reloaded instead. */
  function refreshCartMarkup() {
    rerenderCartDrawer();

    var settings = (window.themeVariables || {}).settings || {};

    if (settings.pageType === 'cart') {
      window.location.reload();
    }
  }

  /* Same position in the cart, same variant, and the values we just wrote. The
     scan is the fallback for the rare case where the write merged this line
     into another one holding an identical message. */
  function keyAfterWrite(cart, index, item, values) {
    var atIndex = cart.items[index];

    if (atIndex && atIndex.variant_id === item.variant_id && giftMatches(atIndex.properties, values)) {
      return atIndex.key;
    }

    var matches = cart.items.filter(function (candidate) {
      return candidate.variant_id === item.variant_id && giftMatches(candidate.properties, values);
    });

    return matches.length === 1 ? matches[0].key : null;
  }

  function writeGift(container) {
    var key = container.getAttribute('data-line-key');
    var values = collectGift(container);

    return fetchCart()
      .then(function (cart) {
        var index = -1;

        cart.items.forEach(function (item, position) {
          if (item.key === key) {
            index = position;
          }
        });

        // The line was removed while the message was being typed.
        if (index === -1) {
          return null;
        }

        var item = cart.items[index];

        // Nothing new to write — this is also what makes flushing both copies
        // of a line item harmless.
        if (giftMatches(item.properties, values)) {
          return null;
        }

        return fetch(ROOT + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, properties: propertiesWithGift(item, values) }),
          keepalive: true
        })
          .then(function (response) {
            if (!response.ok) {
              throw new Error('cart/change.js rejected the gift message');
            }

            return response.json();
          })
          .then(function (updated) {
            var newKey = keyAfterWrite(updated, index, item, values);

            rekeyLine(key, newKey);
            setGiftStatus(container, 'saved');

            if (!newKey) {
              // This line can no longer be told apart from another one, so the
              // keys on screen cannot be trusted — render the cart again.
              refreshCartMarkup();
            } else if (!container.isConnected) {
              // The cart re-rendered while this message was being typed, so what
              // is on screen is one edit behind what was just saved.
              rerenderCartDrawer();
            }

            return updated;
          });
      })
      .catch(function () {
        setGiftStatus(container, 'error');

        return null;
      });
  }

  function saveGift(container) {
    clearTimeout(giftTimers.get(container));

    return enqueue(function () {
      return writeGift(container);
    });
  }

  function scheduleGift(container) {
    clearTimeout(giftTimers.get(container));
    giftTimers.set(
      container,
      setTimeout(function () {
        saveGift(container);
      }, DEBOUNCE)
    );
  }

  function flushGiftLines() {
    var pending = [];

    document.querySelectorAll('[data-gift-line]').forEach(function (container) {
      pending.push(saveGift(container));
    });

    return Promise.all(pending);
  }

  /* The gift message used to be one cart-wide field saved as cart attributes.
     A cart from before the change still carries them, and they would show up on
     the order next to the per item properties, so they are dropped once. */
  var legacyCleared = false;

  function clearLegacyGiftAttributes() {
    if (legacyCleared || !document.querySelector('[data-legacy-gift-attributes]')) {
      return;
    }

    legacyCleared = true;

    enqueue(function () {
      return fetch(ROOT + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { 'Is Gift': '', 'Gift To': '', 'Gift From': '', 'Gift Message': '' }
        })
      }).catch(function () {});
    });
  }

  /* ------------------------------------------------------------------
     Dispatch date helpers
     ------------------------------------------------------------------ */

  function pad(number) {
    return number < 10 ? '0' + number : String(number);
  }

  /* ISO dates compare correctly as plain strings, so they are used throughout. */
  function toIso(date) {
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  function parseIso(iso) {
    var parts = String(iso).split('-');

    if (parts.length !== 3) {
      return null;
    }

    // Built from parts rather than Date.parse, which reads ISO dates as UTC.
    var date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));

    return isNaN(date.getTime()) ? null : date;
  }

  function isoInDays(days) {
    var date = new Date();

    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + days);

    return toIso(date);
  }

  /* Weekday numbers, matching Date#getDay: 0 is Sunday, 6 is Saturday. */
  function closedDaysOf(input) {
    return (input.getAttribute('data-dispatch-closed-days') || '')
      .split(',')
      .map(function (day) {
        return parseInt(day.trim(), 10);
      })
      .filter(function (day) {
        return !isNaN(day) && day >= 0 && day <= 6;
      });
  }

  function nextOpenIso(iso, closed) {
    var date = parseIso(iso);

    // Every day closed would loop forever, and is a misconfiguration anyway.
    if (!date || closed.length === 0 || closed.length >= 7) {
      return iso;
    }

    for (var i = 0; i < 7 && closed.indexOf(date.getDay()) !== -1; i++) {
      date.setDate(date.getDate() + 1);
    }

    return toIso(date);
  }

  /* One dialog is built lazily and reused. It lives on <body> rather than next
     to the input, because the cart drawer replaces its own markup, and because
     showModal() promotes it to the top layer — above the drawer, whatever its
     z-index. */
  var dialog = null;
  var dialogSource = null;

  function ensureDialog() {
    if (dialog) {
      return dialog;
    }

    dialog = document.createElement('dialog');
    dialog.className = 'dispatch-dialog';
    dialog.innerHTML =
      '<p class="dispatch-dialog__heading" data-dispatch-dialog="heading"></p>' +
      '<p class="dispatch-dialog__message" data-dispatch-dialog="message"></p>' +
      '<form method="dialog">' +
      '<button type="submit" class="dispatch-dialog__button" data-dispatch-dialog="button"></button>' +
      '</form>';

    // Otherwise Escape closes the cart drawer underneath along with the dialog.
    dialog.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
      }
    });

    // The field was cleared, so put the customer straight back on it.
    dialog.addEventListener('close', function () {
      if (dialogSource && dialogSource.isConnected) {
        dialogSource.focus();
      }
    });

    document.body.appendChild(dialog);

    return dialog;
  }

  function openClosedDayDialog(input) {
    var heading = input.getAttribute('data-dispatch-closed-heading') || '';
    var message = input.getAttribute('data-dispatch-closed-message') || '';

    var node = ensureDialog();

    dialogSource = input;

    if (typeof node.showModal !== 'function') {
      window.alert(message);
      input.focus();
      return;
    }

    var headingNode = node.querySelector('[data-dispatch-dialog="heading"]');

    headingNode.textContent = heading;
    headingNode.hidden = heading === '';
    node.querySelector('[data-dispatch-dialog="message"]').textContent = message;
    node.querySelector('[data-dispatch-dialog="button"]').textContent =
      input.getAttribute('data-dispatch-closed-button') || 'OK';

    if (!node.open) {
      node.showModal();
    }
  }

  /* A native date input cannot grey out individual weekdays, so a closed day is
     cleared and the customer is asked to pick another one. Returns true when
     the value was rejected.

     `announce` is off for rejections the customer did not trigger — a stored
     date going stale on page load should not throw a dialog at them. */
  function rejectClosedDay(input, announce) {
    if (!input.value) {
      return false;
    }

    var date = parseIso(input.value);
    var min = input.getAttribute('min') || '';
    var max = input.getAttribute('max') || '';

    var invalid =
      !date ||
      input.value < min ||
      (max !== '' && input.value > max) ||
      closedDaysOf(input).indexOf(date.getDay()) !== -1;

    if (!invalid) {
      return false;
    }

    input.value = '';

    if (announce) {
      openClosedDayDialog(input);
    }

    return true;
  }

  function refreshDateLimits() {
    var changed = [];

    document.querySelectorAll('[data-dispatch-lead-days]').forEach(function (input) {
      var lead = parseInt(input.getAttribute('data-dispatch-lead-days'), 10);
      var max = parseInt(input.getAttribute('data-dispatch-max-days'), 10);
      var closed = closedDaysOf(input);
      var serverMin = input.getAttribute('min') || '';
      var min = isoInDays(isNaN(lead) ? 1 : lead);

      // The Liquid `min` is server-derived and cacheable, so the later one wins.
      input.setAttribute('min', nextOpenIso(serverMin > min ? serverMin : min, closed));

      if (!isNaN(max) && max > 0) {
        input.setAttribute('max', isoInDays(max));
      }

      // Catches a date picked earlier that has since passed or become closed.
      if (rejectClosedDay(input)) {
        var group = groupOf(input);

        if (group && changed.indexOf(group) === -1) {
          changed.push(group);
        }
      }
    });

    changed.forEach(flush);
  }

  /* Checkout shows the raw property value, so send something readable. */
  function formatDate(iso) {
    var date = parseIso(iso);

    if (!date) {
      return iso;
    }

    try {
      return date.toLocaleDateString(document.documentElement.lang || undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return iso;
    }
  }

  /* ------------------------------------------------------------------
     Checkout: copy the dispatch date onto every line item as a property
     ------------------------------------------------------------------ */

  function lineValue(key, attributes) {
    var value = attributes[key] || '';

    return key === 'Dispatch Date' && value ? formatDate(value) : value;
  }

  /* An empty value removes a property. Everything else on the line — including
     the gift message saved earlier — is carried over. */
  function propertiesFor(item, attributes) {
    var properties = {};

    Object.keys(item.properties || {}).forEach(function (key) {
      if (LINE_PROPERTIES.indexOf(key) === -1) {
        properties[key] = item.properties[key];
      }
    });

    LINE_PROPERTIES.forEach(function (key) {
      properties[key] = lineValue(key, attributes);
    });

    return properties;
  }

  function needsUpdate(item, properties) {
    var current = item.properties || {};

    return LINE_PROPERTIES.some(function (key) {
      return (current[key] || '') !== properties[key];
    });
  }

  /* Sequential on purpose — the Shopify cart rejects concurrent writes. */
  async function syncLineItems(attributes) {
    var cart = await fetchCart();

    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var properties = propertiesFor(item, attributes);

      if (!needsUpdate(item, properties)) {
        continue;
      }

      await fetch(ROOT + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, properties: properties })
      });
    }
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    var submitter = event.submitter;

    if (!form || !form.querySelector || !submitter || submitter.name !== 'checkout') {
      return;
    }

    var dispatch = fieldOf(form, 'dispatch-date');
    // The gift forms sit next to the line items, which on the drawer is outside
    // this form, so they are looked up on the document.
    var hasGiftLines = document.querySelector('[data-gift-line]') !== null;

    if (!dispatch && !hasGiftLines) {
      return;
    }

    event.preventDefault();

    // Safety net: a closed day is normally rejected on `change`, but if one
    // slipped through, stop here rather than sending an unservable date to
    // checkout. The dialog explains why and puts focus back on the field.
    if (dispatch && rejectClosedDay(dispatch, true)) {
      flush(form);
      return;
    }

    submitter.setAttribute('disabled', '');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    var attributes = collect(form);

    clearTimeout(timers.get(form));

    // The form fields are posted along with `checkout`, so make sure they hold
    // exactly what was written to the cart.
    applyToGroup(form, attributes);

    // Never block the sale: go to checkout even if a write fails. Gift messages
    // go first so the dispatch date is written on top of the final line keys.
    flushGiftLines()
      .then(function () {
        return dispatch
          ? enqueue(function () {
              return syncLineItems(attributes);
            })
          : null;
      })
      .catch(function () {})
      .then(function () {
        var passthrough = document.createElement('input');

        passthrough.type = 'hidden';
        passthrough.name = submitter.name;
        passthrough.value = submitter.value || '';
        form.appendChild(passthrough);

        submitter.removeAttribute('disabled');
        form.submit(); // Native submit, so this handler does not run again.
      });
  });

  /* ------------------------------------------------------------------
     Wiring
     ------------------------------------------------------------------ */

  document.addEventListener('change', function (event) {
    var target = event.target;

    if (!target || !target.matches) {
      return;
    }

    if (target.matches('[data-gift-toggle]')) {
      var toggled = giftLineOf(target);

      if (!toggled) {
        return;
      }

      revealGift(toggled, target.checked);

      if (target.checked) {
        var first = giftFieldOf(toggled, 'to');

        if (first) {
          first.focus();
        }

        // Nothing to store yet — the fields are empty.
        mirrorGift(toggled);
      } else {
        // Unticking drops this item's gift properties.
        applyGift(toggled, { 'Gift To': '', 'Gift From': '', 'Gift Message': '' }, false);
        mirrorGift(toggled);
        saveGift(toggled);
      }

      return;
    }

    if (target.matches('[data-gift-field]')) {
      var edited = giftLineOf(target);

      if (edited) {
        mirrorGift(edited);
        saveGift(edited);
      }

      return;
    }

    if (target.matches('[data-cart-field]')) {
      var group = groupOf(target);

      if (target.matches('[data-cart-field="dispatch-date"]')) {
        rejectClosedDay(target, true);
      }

      if (group) {
        flush(group);
      }
    }
  });

  document.addEventListener('input', function (event) {
    var target = event.target;

    if (!target || !target.matches) {
      return;
    }

    if (target.matches('[data-gift-field]')) {
      var container = giftLineOf(target);

      if (container) {
        updateGiftCounter(container);
        mirrorGift(container);
        scheduleGift(container);
      }

      return;
    }

    if (target.matches('[data-cart-field]')) {
      var group = groupOf(target);

      if (group) {
        schedule(group);
      }
    }
  });

  /* On the cart page the gift fields sit inside the cart form, where Enter
     would submit it — and the only submit button left is checkout. */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' || !event.target || !event.target.matches) {
      return;
    }

    if (!event.target.matches('input[data-gift-field]')) {
      return;
    }

    event.preventDefault();

    var container = giftLineOf(event.target);

    if (container) {
      saveGift(container);
    }
  });

  function onCartRendered() {
    refreshDateLimits();
    clearLegacyGiftAttributes();
  }

  // The drawer re-renders from Liquid, so the limits have to be re-applied.
  document.addEventListener('cart-drawer:refreshed', onCartRendered);
  document.addEventListener('cart:change', onCartRendered);
  document.addEventListener('DOMContentLoaded', onCartRendered);
  onCartRendered();

  /* Coming back from checkout can restore a cached cart page whose quantity
     controls still hold the line keys from before the dispatch date was written
     onto every line item. */
  window.addEventListener('pageshow', function (event) {
    var page = document.querySelector('.cart-page');

    if (!event.persisted || !page) {
      return;
    }

    var dispatch = fieldOf(page, 'dispatch-date');

    if (dispatch && dispatch.value) {
      window.location.reload();
    }
  });
})();
